<template>
    <div ref="raiz">
        <component :is="articleComponent"/>
    </div>
</template>

<script>
import {defineAsyncComponent, h} from 'vue'
import i18n from '@/i18n'

/**
 * Carga un artículo .vue de help/articles/<locale>/<path>.vue
 * Si la versión del idioma activo no existe, hace fallback a 'es'.
 * Si tampoco existe, muestra un error legible.
 */
function buildArticleLoader(path) {
    const locale = i18n.global.locale.value || 'es'
    return defineAsyncComponent({
        loader: async () => {
            try {
                return await import(`./articles/${locale}/${path}.vue`)
            } catch (e) {
                if (locale !== 'es') {
                    try {
                        return await import(`./articles/es/${path}.vue`)
                    } catch (_) { /* fallthrough */ }
                }
                return {
                    render() {
                        return h('div', {class: 'helpArticleError'}, i18n.global.t('help.loadError'))
                    },
                }
            }
        },
        loadingComponent: {
            render() {
                return h('div', {class: 'helpArticleLoading'}, [
                    h('i', {class: 'ti ti-loader-2 helpArticleSpinner'}),
                    ' ',
                    i18n.global.t('help.loading'),
                ])
            },
        },
        delay: 100,
    })
}

export default {
    name: 'HelpArticleViewer',
    props: {
        path: {type: String, required: true},
        // id de una HelpSeccion del artículo: se baja hasta ella y se resalta
        seccion: {type: String, default: ''},
    },
    computed: {
        articleComponent() {
            return buildArticleLoader(this.path)
        },
    },
    mounted() {
        if (this.seccion) this.irASeccion(this.seccion)
    },
    beforeUnmount() {
        clearTimeout(this.timerSeccion)
    },
    methods: {
        // El artículo se carga de forma asíncrona: se espera a que la sección exista (hasta 3 s).
        irASeccion(id, intentos = 60) {
            const el = this.$refs.raiz && this.$refs.raiz.querySelector('[data-seccion="' + CSS.escape(id) + '"]')
            if (!el) {
                if (intentos > 0) this.timerSeccion = setTimeout(() => this.irASeccion(id, intentos - 1), 50)
                return
            }
            el.scrollIntoView({behavior: 'smooth', block: 'start'})
            el.classList.add('helpSeccion--resaltada')
            this.timerSeccion = setTimeout(() => el.classList.remove('helpSeccion--resaltada'), 2200)
        },
    },
}
</script>

<style>
.helpArticleLoading {
    text-align: center;
    color: var(--cui-gray-600, #6e7278);
    padding: 2rem 1rem;
}

.helpArticleSpinner {
    display: inline-block;
    animation: helpSpin 1s linear infinite;
    margin-right: 0.4em;
}

.helpArticleError {
    text-align: center;
    color: var(--cui-danger, #b73135);
    padding: 2rem 1rem;
}

@keyframes helpSpin {
    to { transform: rotate(360deg); }
}
</style>
